import { Component, ReactNode } from 'react'
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Code,
  useColorModeValue,
} from '@chakra-ui/react'
import { WarningIcon } from '@chakra-ui/icons'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onReset?: () => void
  section?: string
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary component that catches JavaScript errors in child components.
 * Displays a fallback UI instead of crashing the entire app.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    // In production, you would send this to an error reporting service
    // e.g., Sentry, LogRocket, etc.
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
    this.props.onReset?.()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <ErrorFallback
          error={this.state.error}
          section={this.props.section}
          onReset={this.handleReset}
        />
      )
    }

    return this.props.children
  }
}

interface ErrorFallbackProps {
  error: Error | null
  section?: string
  onReset: () => void
}

function ErrorFallback({ error, section, onReset }: ErrorFallbackProps) {
  const bgColor = useColorModeValue('red.50', 'red.900')
  const borderColor = useColorModeValue('red.200', 'red.700')
  const textColor = useColorModeValue('red.800', 'red.100')

  return (
    <Box
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      p={6}
      role="alert"
      aria-live="assertive"
    >
      <VStack spacing={4} align="start">
        <VStack spacing={2} align="start">
          <Heading as="h3" size="sm" color={textColor} display="flex" alignItems="center" gap={2}>
            <WarningIcon />
            {section ? `Error in ${section}` : 'Something went wrong'}
          </Heading>
          <Text color={textColor} fontSize="sm">
            An unexpected error occurred. You can try again or refresh the page.
          </Text>
        </VStack>

        {error && (
          <Code
            display="block"
            whiteSpace="pre-wrap"
            p={3}
            borderRadius="md"
            fontSize="xs"
            maxH="100px"
            overflowY="auto"
            w="100%"
          >
            {error.message}
          </Code>
        )}

        <Button size="sm" colorScheme="red" variant="outline" onClick={onReset}>
          Try again
        </Button>
      </VStack>
    </Box>
  )
}

export default ErrorBoundary
